import { z } from 'zod';
import {
  memoryModulesForProposal,
  type MemoryModule,
  type MemoryProposalPayload,
  type MemoryRuleAddress,
  type SenderRouteKind,
} from '@erp/shared-types';

export type ProposeMemoryMode =
  | 'note'
  | 'customer_alias'
  | 'carrier_term_alias'
  | 'location_is_own_site'
  | 'sender_route'
  | 'extraction_hint';

/** Where a taught memory is filed unless the person picks another area. */
export const DEFAULT_PROPOSE_MEMORY_MODULE: MemoryModule = 'oms';

/**
 * The areas the person can file a memory of this mode under: only those the
 * server accepts for it (a name alias, the one the form builds, can go to
 * Sales orders or Remittances; an extraction hint to any area; everything
 * else stays in Sales orders).
 */
export function proposeMemoryModuleOptions(
  mode: ProposeMemoryMode,
): MemoryModule[] {
  return memoryModulesForProposal({
    kind: mode,
    aliasKind: mode === 'customer_alias' ? 'name' : undefined,
  });
}

/** The picked area when the mode allows it, else the default. */
export function effectiveProposeMemoryModule(
  mode: ProposeMemoryMode,
  picked: MemoryModule,
): MemoryModule {
  return proposeMemoryModuleOptions(mode).includes(picked)
    ? picked
    : DEFAULT_PROPOSE_MEMORY_MODULE;
}

/** Which documents a sender route or extraction hint applies to. */
export const ProposeMemoryDocumentScopeSchema = z.enum([
  'all',
  'purchase_order',
  'discount_request',
]);
export type ProposeMemoryDocumentScope = z.infer<
  typeof ProposeMemoryDocumentScopeSchema
>;

/** The scope matching an email's classification label, else every document. */
export function documentScopeFromClassification(
  classification: string | null | undefined,
): ProposeMemoryDocumentScope {
  const parsed = ProposeMemoryDocumentScopeSchema.safeParse(
    classification?.trim().toLowerCase(),
  );
  return parsed.success ? parsed.data : 'all';
}

/** The fields of the two routing kinds (sender route, extraction hint). */
export interface ProposeMemoryRoutingFields {
  /** Sender route: the address or domain as typed. */
  sender: string;
  senderKind: SenderRouteKind;
  targetOrganizationId: string | null;
  documentScope: ProposeMemoryDocumentScope;
  /** Extraction hint: dotted field path (`*` for the whole document). */
  fieldPath: string;
  senderDomain: string;
}

export const EMPTY_ADDRESS: MemoryRuleAddress = {
  street: null,
  city: null,
  postalCode: null,
  country: null,
};

/** What a host pre-fills the form with (a draft's facts, a task's email, a mode). */
export interface ProposeMemorySeed {
  mode?: ProposeMemoryMode;
  customerId?: string | null;
  extractedNames?: string[];
  extractedShippingText?: string | null;
  destinationAddress?: MemoryRuleAddress | null;
  sender?: string;
  documentScope?: ProposeMemoryDocumentScope;
}

export function initialProposeMemoryValues(seed: ProposeMemorySeed): {
  mode: ProposeMemoryMode;
  customerId: string | null;
  alias: string;
  term: string;
  address: MemoryRuleAddress;
  routing: ProposeMemoryRoutingFields;
} {
  return {
    mode: seed.mode ?? 'note',
    customerId: seed.customerId ?? null,
    alias: seed.extractedNames?.[0] ?? '',
    term: seed.extractedShippingText ?? '',
    address: seed.destinationAddress ?? EMPTY_ADDRESS,
    routing: initialRoutingFields({
      sender: seed.sender,
      documentScope: seed.documentScope,
    }),
  };
}

/**
 * The address an own-site memory is about: what the person typed when the
 * form lets them (settings), else the draft's ship-to when it has one.
 */
export function knownDestinationAddress(params: {
  allowAddressEntry: boolean | undefined;
  address: MemoryRuleAddress;
  destinationAddress: MemoryRuleAddress | null | undefined;
}): MemoryRuleAddress | null {
  if (params.allowAddressEntry) {
    return params.address.street || params.address.city ? params.address : null;
  }
  return params.destinationAddress ? params.address : null;
}

export function initialRoutingFields(
  init: { sender?: string; documentScope?: ProposeMemoryDocumentScope } = {},
): ProposeMemoryRoutingFields {
  return {
    sender: init.sender ?? '',
    senderKind: 'email_domain',
    targetOrganizationId: null,
    documentScope: init.documentScope ?? 'all',
    fieldPath: '',
    senderDomain: '',
  };
}

export interface ProposeMemoryInput {
  mode: ProposeMemoryMode;
  customerId: string | null;
  /** Note text, routing note, or extraction hint, depending on the mode. */
  text: string;
  alias: string;
  term: string;
  code: string;
  label: string;
  destinationAddress: MemoryRuleAddress | null;
  routing: ProposeMemoryRoutingFields;
  /**
   * An extraction hint is limited to `customerId` (the person picked it in
   * the form). Off where the customer is only the draft's, so a hint taught
   * from a draft keeps applying to every customer.
   */
  limitHintToCustomer?: boolean;
}

function documentTypesFor(scope: ProposeMemoryDocumentScope): string[] | null {
  return scope === 'all' ? null : [scope];
}

/** The bare address inside a From header such as `Name <addr@x>`, lowercased; empty when there is none. */
export function senderAddressOf(sender: string): string {
  return sender.match(/[^\s<>"]+@[^\s<>"]+/)?.[0]?.toLowerCase() ?? '';
}

/** The domain of the address in `sender`, or the text itself when it is a bare domain. */
export function senderDomainOf(sender: string): string {
  const address = senderAddressOf(sender);
  if (address) return address.slice(address.lastIndexOf('@') + 1);
  return sender.trim().toLowerCase();
}

/** What the title names: the target organization for a route, else the customer. */
export function proposalLabelName(
  payload: MemoryProposalPayload,
  customerName: string | null,
  organizations: ReadonlyArray<{ id: string; name: string }>,
): string | null {
  if (payload.kind !== 'sender_route') return customerName;
  return (
    organizations.find((org) => org.id === payload.targetOrganizationId)
      ?.name ?? null
  );
}

type PayloadBuilder = (
  input: ProposeMemoryInput,
) => MemoryProposalPayload | null;

function notePayload(input: ProposeMemoryInput): MemoryProposalPayload | null {
  const note = input.text.trim();
  return note
    ? {
        kind: 'note',
        note,
        customerId: input.customerId,
        customerEmailDomain: null,
      }
    : null;
}

function customerAliasPayload(
  input: ProposeMemoryInput,
): MemoryProposalPayload | null {
  const alias = input.alias.trim();
  return input.customerId && alias
    ? {
        kind: 'customer_alias',
        aliasKind: 'name',
        alias,
        customerId: input.customerId,
      }
    : null;
}

function carrierTermPayload(
  input: ProposeMemoryInput,
): MemoryProposalPayload | null {
  const term = input.term.trim();
  const code = input.code.trim().toUpperCase();
  return term && code
    ? {
        kind: 'carrier_term_alias',
        term,
        shippingMethodCode: code,
        customerId: input.customerId,
      }
    : null;
}

function ownSitePayload(
  input: ProposeMemoryInput,
): MemoryProposalPayload | null {
  const label = input.label.trim();
  return input.destinationAddress && label
    ? { kind: 'location_is_own_site', label, address: input.destinationAddress }
    : null;
}

/** A route needs a sender plus a target organization or a note. */
function senderRoutePayload(
  input: ProposeMemoryInput,
): MemoryProposalPayload | null {
  const { routing } = input;
  const sender =
    routing.senderKind === 'email_domain'
      ? senderDomainOf(routing.sender)
      : senderAddressOf(routing.sender);
  const note = input.text.trim();
  if (!sender || (!routing.targetOrganizationId && !note)) return null;
  return {
    kind: 'sender_route',
    senderKind: routing.senderKind,
    sender,
    targetOrganizationId: routing.targetOrganizationId,
    note: note || null,
    documentTypes: documentTypesFor(routing.documentScope),
  };
}

function extractionHintPayload(
  input: ProposeMemoryInput,
): MemoryProposalPayload | null {
  const { routing } = input;
  const fieldPath = routing.fieldPath.trim();
  const hint = input.text.trim();
  if (!fieldPath || !hint) return null;
  const senderDomain = senderDomainOf(routing.senderDomain);
  const limitedTo =
    input.limitHintToCustomer && input.customerId ? [input.customerId] : null;
  return {
    kind: 'extraction_hint',
    fieldPath,
    hint,
    documentTypes: documentTypesFor(routing.documentScope),
    senderDomain: senderDomain || null,
    ...(limitedTo ? { customerIds: limitedTo } : {}),
  };
}

const PAYLOAD_BUILDERS: Record<ProposeMemoryMode, PayloadBuilder> = {
  note: notePayload,
  customer_alias: customerAliasPayload,
  carrier_term_alias: carrierTermPayload,
  location_is_own_site: ownSitePayload,
  sender_route: senderRoutePayload,
  extraction_hint: extractionHintPayload,
};

/** The proposal payload for the dialog's current state, or null while incomplete. */
export function buildProposeMemoryPayload(
  input: ProposeMemoryInput,
): MemoryProposalPayload | null {
  return PAYLOAD_BUILDERS[input.mode](input);
}

type Translate = (
  text: string,
  options?: Record<string, string | undefined>,
) => string;

/** Short title for a proposal made from a draft; `t` is the gt translator. */
export function proposeMemoryTitle(
  payload: MemoryProposalPayload,
  customerName: string | null,
  t: Translate,
): string {
  switch (payload.kind) {
    case 'note':
      return t('Note about {customer}', {
        customer: customerName ?? t('this order'),
        context: 'auto title for a memory note proposal',
      });
    case 'customer_alias':
      return t('“{alias}” is {customer}', {
        alias: payload.alias,
        customer: customerName ?? payload.customerId,
        context: 'auto title for a customer alias proposal',
      });
    case 'carrier_term_alias':
      return t('“{term}” means {code}', {
        term: payload.term,
        code: payload.shippingMethodCode,
        context: 'auto title for a carrier term proposal',
      });
    case 'location_is_own_site':
      return t('{label} is our own site', {
        label: payload.label,
        context: 'auto title for an own-site proposal',
      });
    case 'sender_route':
      return payload.targetOrganizationId
        ? t('Route {sender} to {organization}', {
            sender: payload.sender,
            organization: customerName ?? payload.targetOrganizationId,
            context: 'auto title for a sender route proposal',
          })
        : t('Routing note about {sender}', {
            sender: payload.sender,
            context: 'auto title for a sender routing note proposal',
          });
    case 'extraction_hint':
      return t('Extraction hint on {field}', {
        field: payload.fieldPath,
        context: 'auto title for an extraction hint proposal',
      });
    default:
      return payload.kind;
  }
}
